import { NavLink } from 'react-router-dom';
import {
  IoHomeOutline,
  IoCubeOutline,
  IoMailOutline,
  IoWalletOutline,
  IoSparklesOutline,
  IoPersonOutline,
} from 'react-icons/io5';
import { cn } from '@/components/ui/utils';
import { useInboxUnreadCount } from '@/hooks/useInbox';

/**
 * Desktop sidebar navigation for the consumer web app (PEL-817).
 *
 * Layout per style guide §10 + mobile parity:
 * - Full-width on desktop (≥1024px): shows icon + label.
 * - Icon-only rail on tablet (768–1023px): label hidden via md:inline / lg:hidden tricks.
 * - Hidden entirely on mobile (<768px) — BottomTabBar takes over there.
 *
 * Active route: left accent bar in --brand + --brand-light background + --brand text.
 * Inactive: --text-light on hover into --brand-light/40.
 */

type NavItem = {
  to: string;
  label: string;
  Icon: typeof IoHomeOutline;
};

const NAV_ITEMS: NavItem[] = [
  { to: '/app', label: 'Home', Icon: IoHomeOutline },
  { to: '/app/orders', label: 'Orders', Icon: IoCubeOutline },
  { to: '/app/inbox', label: 'Inbox', Icon: IoMailOutline },
  { to: '/app/wallet', label: 'Wallet', Icon: IoWalletOutline },
  { to: '/app/pelipoints', label: 'PeliPoints', Icon: IoSparklesOutline },
  { to: '/app/profile', label: 'Profile', Icon: IoPersonOutline },
];

export function Sidebar() {
  const { data: unread } = useInboxUnreadCount();
  const unreadCount = unread?.count ?? 0;

  return (
    <aside
      className={cn(
        'hidden md:flex',
        'flex-col',
        'w-16 lg:w-60',
        'shrink-0',
        'bg-[var(--surface)] border-r border-[var(--border)]',
        'h-screen sticky top-0',
      )}
      aria-label="Primary navigation"
    >
      {/* Brand wordmark — official Pelipost logo */}
      <div className="h-16 flex items-center px-4 border-b border-[var(--border)]">
        {/* Desktop: full horizontal logo (orange square + black Pelipost wordmark) */}
        <img
          src="/logo-horizontal.png"
          alt="Pelipost"
          className="hidden lg:block h-7 w-auto"
        />
        {/* Tablet rail: icon-only square pelican */}
        <img
          src="/logo-icon.png"
          alt="Pelipost"
          className="inline lg:hidden w-8 h-8"
        />
      </div>

      {/* Nav list */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-1 px-2">
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/app'}
                className={({ isActive }) =>
                  cn(
                    'group relative flex items-center gap-3',
                    'px-3 py-2.5 rounded-[var(--radius-lg)]',
                    'text-[14px] font-medium',
                    'transition-colors',
                    isActive
                      ? 'bg-[var(--brand-light)] text-[var(--brand)]'
                      : 'text-[var(--text-light)] hover:bg-[var(--brand-light)]/50 hover:text-[var(--brand)]',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Active left accent bar */}
                    {isActive && (
                      <span
                        className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-[var(--brand)]"
                        aria-hidden
                      />
                    )}
                    <span className="relative shrink-0">
                      <Icon size={22} aria-hidden />
                      {label === 'Inbox' && unreadCount > 0 && (
                        <span
                          aria-label={`${unreadCount} unread`}
                          className="absolute -top-1.5 -right-2 flex min-w-[16px] h-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white"
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </span>
                    <span className="hidden lg:inline truncate">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer / version stamp */}
      <div className="border-t border-[var(--border)] px-4 py-3 hidden lg:block">
        <p className="text-[11px] text-[var(--text-muted)] leading-tight">
          app.pelipost.com
        </p>
      </div>
    </aside>
  );
}
